/**
 *  Widgets - 2way handicap tennis
 *  @module Widgets
 *  @author Livescore <info@score-stats.com>
 *  @copyright 2022 livescore
 */

import { MatchService } from '@services/match.service';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from '@widgets/pages/base.component';
import { Observable, Subscription, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap } from 'rxjs/operators';

import { DemoService } from '@widgets/services/demo.service';
import { WidgetMixin } from '@widgets/services/mixins/widget.mixin';

import { Form, LastForm } from '@widgets/interfaces/widget.interface';

import CONFIG from '@widgets/config/widget.config';

import { MatchType } from '@/interfaces/league-table.interface';
import { ClientService } from '@/services/client.service';
import { CodeState, MatchData, Team } from '@/interfaces/match-data.interface';
import { LangService } from '@/services/lang.service';

@Component({
    selector: 'widgets-total-games-tennis',
    templateUrl: './total-games-tennis.html',
    styleUrls: ['../styles.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class TotalGamesTennisComponent extends WidgetMixin(BaseComponent) implements OnInit, OnDestroy {
    public matches: MatchData[] = [];

    private matchId: number;

    private subscriber: Subscription;

    public data: Form | null = null;

    public type: string = 'default';

    private periods: string[] = [
        'score_period_1',
        'score_period_2',
        'score_period_3',
        'score_period_4',
        'score_period_5',
    ];

    public constructor(
        private router: ActivatedRoute,
        private match: MatchService,
        private lang: LangService,
        private demo: DemoService,
        public client: ClientService,
        private ref: ChangeDetectorRef,
    ) {
        super(demo);
    }

    /**
     * Load data from server
     */
    public ngOnInit(): void {
        super.ngOnInit();

        this.setTitle(this.router);

        this.router.data.subscribe((sub) => {
            this.num = sub.num;

            if (sub.br && sub.br.length > 0) {
                [this.matchId] = sub.br;
            } else {
                this.finished();
            }

            if (this.matchId) {
                this.load();
            } else {
                this.error();
            }
        });
    }

    public ngOnDestroy(): void {
        if (this.subscriber instanceof Subscription) {
            this.subscriber.unsubscribe();
        }

        this.matches.splice(0);
    }

    public getPlayed(team: 'home' | 'away'): number {
        let gameTotal = 0;
        let total = 0;

        const reversed =
            this.data![team].form.length > this.num ? this.data![team].form.slice(1) : this.data![team].form;
        reversed.forEach((f) => {
            if (f.code_state === CodeState.ENDED && f.score) {
                this.periods.forEach((period: string): void => {
                    if (f[period] && f[period] !== null) {
                        const [homePoint, awayPoint] = f[period].split(':');

                        gameTotal += +homePoint + +awayPoint;
                    }
                });


                total += 1;
            }
        });


        return (gameTotal / total) || 0;
    }

    get homeTotal(): number {
        return this.getPlayed('home');
    }

    get awayTotal(): number {
        return this.getPlayed('away');
    }


    get widthHomeTotal(): number {
        const total = this.homeTotal + this.awayTotal;
        return Math.round((this.homeTotal / total) * 100);
    }

    get widthAwayTotal(): number {
        return 100 - this.widthHomeTotal;
    }


    private load(): void {
        const iso: string | null = this.lang.getLangSnapshot();
        let homeId: number | null | undefined = null;
        let awayId: number | null | undefined = null ?? undefined;

        this.subscriber = this.match
            .getMatchNew(this.matchId, iso as string, true)
            .pipe(
                filter((match: MatchData) => CONFIG.hadndicap2way.availSports.includes(match.sport_code_name)),
                mergeMap((match: MatchData): Observable<Form> => {
                    const [p1, p2] = match.participants.map((p: Team): number => p.id);
                    homeId = p1;
                    awayId = p2;
                    return this.demo.form(p1, p2, iso as string, { limit: this.num });
                }),
                map((data: Form) => {
                    data.home = { ...data.home, id: homeId };
                    data.away = { ...data.away, id: awayId };

                    data.home.form = data.home.form.filter((f: LastForm) => f.type !== MatchType.UPCOMING);
                    data.away.form = data.away.form.filter((f: LastForm) => f.type !== MatchType.UPCOMING);
                    return data;
                }),
                switchMap(this.checkEmpty()),
                catchError((e): Observable<any> => {
                    this.error(e);
                    return throwError(e);
                }),
            )

            .subscribe(
                (data) => {
                    this.data = data;
                },
                () => {
                    this.error();
                },
                () => {
                    if (!this.data) {
                        this.error();
                    }
                    this.loaded = true;
                    this.finished();
                },
            );
    }
}
